import { useMemo } from 'react'
import { useAppSelector } from '@/app/hooks'

/**
 * The module-driven access pattern. Every gated feature in the portal
 * checks three things, in order:
 *
 *   1. Permission — does the user's role grant this permission code?
 *   2. Module     — has the tenant enabled this module?
 *   3. Industry   — (only for industry-specific modules) does the tenant's
 *                   template include it? Enforced implicitly via module
 *                   enablement, since templates drive which modules are on.
 *
 * Super_admins do NOT bypass tenant permission/module gates in the UI —
 * they have no tenant context, so tenant pages would 422 if they tried.
 * Platform users (super_admin / platform_staff) use the separate admin console,
 * not this tenant portal (RequireAuth intercepts them).
 */

export function useCan(permission: string | string[]): boolean {
  const user = useAppSelector((s) => s.auth.user)
  return useMemo(() => {
    if (!user) return false
    const required = Array.isArray(permission) ? permission : [permission]
    return required.some((p) => user.permissions.includes(p))
  }, [user, permission])
}

export function useCanAll(permissions: string[]): boolean {
  const user = useAppSelector((s) => s.auth.user)
  return useMemo(() => {
    if (!user) return false
    return permissions.every((p) => user.permissions.includes(p))
  }, [user, permissions])
}

export function useModule(moduleCode: string | string[]): boolean {
  const company = useAppSelector((s) => s.auth.company)
  return useMemo(() => {
    if (!company) return false
    const required = Array.isArray(moduleCode) ? moduleCode : [moduleCode]
    return required.every((m) => company.enabled_modules.includes(m))
  }, [company, moduleCode])
}

interface CanAccessArgs {
  permission?: string | string[]
  module?: string | string[]
  /** Passes if ANY one of these modules is enabled (vs `module` = ALL). */
  moduleAny?: string[]
  /** Require ALL permissions instead of ANY. */
  requireAll?: boolean
}

/**
 * Composable check combining permission + module gates.
 * Use for sidebar visibility and route guards.
 *
 * Super_admins get false for tenant-scoped pages — they have no tenant
 * context and would 422 if they tried. They use the separate admin console
 * surface instead.
 */
export function useCanAccess({ permission, module, moduleAny, requireAll }: CanAccessArgs): boolean {
  const user = useAppSelector((s) => s.auth.user)
  const company = useAppSelector((s) => s.auth.company)

  return useMemo(() => {
    if (!user) return false
    // Platform users (super_admin / platform_staff) have no tenant context —
    // tenant pages would 422. They get the platform surface instead.
    if (user.user_type === 'super_admin' || user.user_type === 'platform_staff') return false

    // Module gate (ALL required)
    if (module) {
      const required = Array.isArray(module) ? module : [module]
      const enabled = company?.enabled_modules ?? []
      const moduleOk = required.every((m) => enabled.includes(m))
      if (!moduleOk) return false
    }

    // Module gate (ANY of — e.g. show only if at least one gateway is enabled)
    if (moduleAny && moduleAny.length > 0) {
      const enabled = company?.enabled_modules ?? []
      if (!moduleAny.some((m) => enabled.includes(m))) return false
    }

    // Permission gate
    if (permission) {
      const required = Array.isArray(permission) ? permission : [permission]
      const ok = requireAll
        ? required.every((p) => user.permissions.includes(p))
        : required.some((p) => user.permissions.includes(p))
      if (!ok) return false
    }

    return true
  }, [user, company, permission, module, moduleAny, requireAll])
}

export function useUserType() {
  return useAppSelector((s) => s.auth.user?.user_type ?? null)
}

/**
 * Access check for the DistroOps internal admin surface (platform.* perms).
 * True when the user is platform-level (super_admin OR platform_staff) AND
 * holds the required platform permission. super_admin's /me carries every
 * permission code, so it passes everything; platform_staff passes only its
 * assigned platform.* codes. Pass no permission to check membership only.
 */
export function useCanPlatform(permission?: string | string[]): boolean {
  const user = useAppSelector((s) => s.auth.user)
  return useMemo(() => {
    if (!user) return false
    if (user.user_type !== 'super_admin' && user.user_type !== 'platform_staff') return false
    // super_admin is the locked god role — full platform access regardless of
    // granular platform.* grants (mirrors the backend isSuperAdmin() bypass).
    if (user.user_type === 'super_admin') return true
    if (!permission) return true
    const required = Array.isArray(permission) ? permission : [permission]
    return required.some((p) => user.permissions.includes(p))
  }, [user, permission])
}
